import EditIcon from '@persona-id/design-tokens/icons/EditIcon';
import RotateIcon from '@persona-id/design-tokens/icons/RotateCwIcon';
import { isEmpty } from 'lodash/fp';
import { useEffect, useState, VFC } from 'react';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { getTokensToDisplayValue, UsableObjectConfig } from 'dashboard/lib/workflows';
import { BaseTrigger, Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionUpdateAccountFromUserInputV1 } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

const ACCOUNT_OPTIONS = [
  {
    label: 'Account of Trigger Case',
    value: `${BaseTrigger}.${ApiModel.Account}`,
  },
];

const InputFieldsHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const ALLOWED_ACCOUNT_FIELDS = [
  'name_first',
  'name_middle',
  'name_last',
  'birthdate',
  'country_code',
  'address_street_1',
  'address_street_2',
  'address_city',
  'address_subdivision',
  'address_postal_code',
  'phone_number',
  'email_address',
  'identification_class',
  'identification_number',
];

interface ParamsUpdateAccountV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  params: WorkflowActionUpdateAccountFromUserInputV1['params'];
  updateActionParams: (newParams: WorkflowActionUpdateAccountFromUserInputV1['params']) => void;
}

const ParamsUpdateAccountFromUserInputV1: VFC<ParamsUpdateAccountV1Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}: ParamsUpdateAccountV1Props) => {
  const [singleOption, setSingleOption] = useState(false);
  const [showAccountFields, { toggle: toggleAccountFields, disable: closeAccountFields }] =
    useToggle(false);

  useEffect(() => {
    if (ACCOUNT_OPTIONS.length === 1) {
      handleSelectAccount(ACCOUNT_OPTIONS[0].value);
      setSingleOption(true);
    }
    if (isEmpty(params['account-fields'])) {
      handleChangeFieldMappings(getDefaultAccountFieldsMapping(ALLOWED_ACCOUNT_FIELDS));
    }
  }, []);

  const handleSelectAccount = (value: string) => {
    updateActionParams({
      ...params,
      account: value,
    });
  };

  const getDefaultAccountFieldsMapping = (fields: string[]) => {
    return (fields || []).reduce((acc: Record<string, string>, field: string) => {
      acc[field] = `trigger_params.${field}`;
      return acc;
    }, {});
  };

  const handleChangeFieldMappings = (value: Record<string, string>) => {
    updateActionParams({
      ...params,
      'account-fields': value,
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionUpdateAccountFromUserInputV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        htmlFor='params-update-account-from-user-input__account-input'
        labelText='Account'
      />
      <ReactSelect
        value={params['account']}
        options={ACCOUNT_OPTIONS}
        onChange={handleSelectAccount}
        disabled={!editable || singleOption}
        id='params-update-account-from-user-input__account'
        hasError={hasErrorFor('account')}
      />
      {renderErrorMessageFor('account')}
      <Spacer height={4} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        {showAccountFields && (
          <FieldMappingsPanel
            title='Account fields'
            usableObjects={usableObjects}
            fieldMappings={params['account-fields'] ?? {}}
            onOutsideClick={closeAccountFields}
            onUpdateFieldMappings={handleChangeFieldMappings}
          />
        )}
        <Spacer height={2} />
        {editable && (
          <InputFieldsHeading>
            <ParamLabel
              htmlFor='params-update-account-from-user-input__account-fields'
              labelText='Account Fields'
            />
            <HStack>
              <Button
                variant='basic'
                onClick={toggleAccountFields}
                id='params-update-account-from-user-input__account-fields'
              >
                <EditIcon />
              </Button>
              <Button
                variant='basic'
                onClick={() =>
                  handleChangeFieldMappings(getDefaultAccountFieldsMapping(ALLOWED_ACCOUNT_FIELDS))
                }
                id='params-update-account-from-user-input__set-default-fields'
                aria-label='Set default fields'
              >
                <RotateIcon />
              </Button>
            </HStack>
          </InputFieldsHeading>
        )}
        {renderErrorMessageFor('account-fields')}
        <Spacer height={2} />
        <FieldsList
          fields={params['account-fields']}
          fieldKey='account-fields'
          errors={errors}
          editable={editable}
          toggleEditPanel={toggleAccountFields}
          tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
        />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsUpdateAccountFromUserInputV1;
